import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { CommentThread, WhiteboardComment } from '@/features/canvas/types';
import { Member } from '@/features/backlog/types';
import { renderCommentBody } from '@/features/backlog/mentions';
import { commentTime } from '@/features/backlog/format';
import MemberAvatar from '@/components/member-avatar';
import MentionInput from '@/components/mention-input';
import { cn } from '@/components/ui/cn';
import { GLASS } from './glass';
import { MS, MS_ICON } from './ms';

const PANEL_W = 420; // ~33% larger than the original 320
/** The fixed emoji palette offered by the "add reaction" button. */
const REACTIONS = ['👍', '❤️', '😄', '🎉', '😮', '🙏', '👀', '🚀'];

/**
 * The Miro-like thread popover, anchored beside its pin. Shows the thread's comments (author, time,
 * @mention-rendered body) with a reply composer, per-comment edit/delete (author or admin), and a
 * resolve/reopen toggle. When `thread` is null it's a NEW draft — just the composer; submitting drops
 * the pin. Positioned in screen space and clamped to the viewport; the comment list scrolls when the
 * panel hits its max height (80vh).
 */
export default function CommentThreadPopover({
  thread,
  anchor,
  members,
  meId,
  isAdmin,
  onSubmitNew,
  onReply,
  onEdit,
  onDelete,
  onToggleResolve,
  onReact,
  onClose,
}: {
  thread: CommentThread | null;
  anchor: { x: number; y: number };
  members: Member[];
  meId: string;
  isAdmin: boolean;
  onSubmitNew: (body: string) => void;
  onReply: (body: string) => void;
  onEdit: (id: string, body: string) => void;
  onDelete: (id: string) => void;
  onToggleResolve: () => void;
  onReact: (commentId: string, emoji: string) => void;
  onClose: () => void;
}) {
  const membersById = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);
  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [reactingId, setReactingId] = useState<string | null>(null); // which comment's emoji palette is open
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: anchor.x + 28, top: anchor.y - 40 });

  // Clamp the panel into the viewport once it (and its measured height) exist.
  useLayoutEffect(() => {
    const h = panelRef.current?.offsetHeight ?? 360;
    const left = Math.min(anchor.x + 28, window.innerWidth - PANEL_W - 12);
    const top = Math.min(Math.max(anchor.y - 40, 64), window.innerHeight - h - 12);
    setPos({ left: Math.max(12, left), top: Math.max(12, top) });
  }, [anchor.x, anchor.y, thread?.count]);

  // Escape closes — but in the BUBBLE phase, so the reply composer's mention dropdown gets first crack
  // (it stops propagation while its autocomplete is open, so Escape dismisses the dropdown, not the thread).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const canManage = (c: WhiteboardComment) => isAdmin || (!!c.author_id && c.author_id === meId);

  const submitNew = () => {
    const b = draft.trim();
    if (!b) return;
    onSubmitNew(b);
    setDraft('');
  };
  const submitReply = () => {
    const b = draft.trim();
    if (!b) return;
    onReply(b);
    setDraft('');
  };
  const saveEdit = () => {
    const b = editDraft.trim();
    if (b && editingId) onEdit(editingId, b);
    setEditingId(null);
    setEditDraft('');
  };

  // A plain render FUNCTION, not a nested component — a nested component would be a new type every
  // render, remounting every row (and blowing away the inline edit box's focus on each keystroke).
  const renderRow = (c: WhiteboardComment) => {
    const mem = c.author_id ? membersById.get(c.author_id) : undefined;
    const editing = editingId === c.id;
    return (
      <div key={c.id} className="group/row px-4 py-2.5">
        <div className="flex items-center gap-2.5">
          <MemberAvatar member={mem ?? ({ id: c.author_id ?? '', name: c.author_name || 'Someone', color: '#71717a', active: true } as Member)} size={29} />
          <span className="truncate text-[16px] font-medium text-fg">{mem?.name || c.author_name || 'Someone'}</span>
          <span className="text-[15px] text-fg-faint">{commentTime(c.created_at)}</span>
          {canManage(c) && !editing ? (
            <span className="ml-auto flex items-center gap-1 opacity-0 transition-opacity group-hover/row:opacity-100">
              <button
                type="button"
                title="Edit"
                className="flex h-8 w-8 items-center justify-center rounded-md text-fg-muted hover:bg-layer hover:text-fg"
                onClick={() => {
                  setEditingId(c.id);
                  setEditDraft(c.body);
                }}
              >
                <MS icon={MS_ICON.edit} size={17} />
              </button>
              <button
                type="button"
                title="Delete"
                className="flex h-8 w-8 items-center justify-center rounded-md text-fg-muted hover:bg-danger/10 hover:text-danger"
                onClick={() => onDelete(c.id)}
              >
                <MS icon={MS_ICON.delete} size={17} />
              </button>
            </span>
          ) : null}
        </div>
        {editing ? (
          <div className="mt-2 flex flex-col gap-2">
            <MentionInput value={editDraft} onChange={setEditDraft} members={members} onSubmit={saveEdit} placeholder="Edit comment…" size="lg" />
            <div className="flex justify-end gap-2">
              <button type="button" className="rounded-lg px-2.5 py-1.5 text-[16px] text-fg-muted hover:bg-layer" onClick={() => setEditingId(null)}>
                Cancel
              </button>
              <button type="button" className="rounded-lg bg-brand px-3 py-1.5 text-[16px] font-medium text-white hover:bg-brand/90" onClick={saveEdit}>
                Save
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="mt-1 whitespace-pre-wrap break-words pl-[40px] text-[17px] leading-snug text-fg-soft">
              {renderCommentBody(c.body, membersById, meId)}
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5 pl-[40px]">
              {(c.reactions ?? []).map((r) => {
                const mine = r.members.includes(meId);
                return (
                  <button
                    key={r.emoji}
                    type="button"
                    onClick={() => onReact(c.id, r.emoji)}
                    title={mine ? 'Remove your reaction' : 'React'}
                    className={cn(
                      'flex items-center gap-1.5 rounded-full border px-2 py-1 text-[16px] leading-none transition-colors',
                      mine ? 'border-brand/50 bg-brand/15 text-fg' : 'border-line bg-layer/50 text-fg-soft hover:bg-layer',
                    )}
                  >
                    <span>{r.emoji}</span>
                    <span className="text-[15px] tabular-nums">{r.members.length}</span>
                  </button>
                );
              })}
              <button
                type="button"
                title="Add reaction"
                onClick={() => setReactingId(reactingId === c.id ? null : c.id)}
                className={cn(
                  'flex h-[30px] items-center gap-1 rounded-full border border-line px-2 text-[17px] leading-none transition-colors',
                  reactingId === c.id ? 'bg-layer text-fg' : 'text-fg-faint hover:bg-layer hover:text-fg',
                )}
              >
                🙂<span className="text-[15px]">＋</span>
              </button>
              {reactingId === c.id
                ? REACTIONS.map((e) => (
                    <button
                      key={e}
                      type="button"
                      onClick={() => {
                        onReact(c.id, e);
                        setReactingId(null);
                      }}
                      className="flex h-[30px] w-[30px] items-center justify-center rounded-md text-[20px] leading-none transition-colors hover:bg-layer"
                    >
                      {e}
                    </button>
                  ))
                : null}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div
      ref={panelRef}
      data-wb-overlay
      onPointerDown={(e) => e.stopPropagation()}
      className={cn(GLASS, 'pointer-events-auto fixed z-30 flex max-h-[80vh] w-[420px] origin-top-left scale-[0.8] flex-col overflow-hidden rounded-2xl')}
      style={{ left: pos.left, top: pos.top }}
    >
      {/* header */}
      <div className="flex items-center gap-2.5 border-b border-line px-4 py-2.5">
        <MS icon={MS_ICON.comment_dots} size={20} className="text-fg-muted" />
        <span className="text-[16px] font-semibold text-fg">{thread ? 'Comment thread' : 'New comment'}</span>
        <div className="ml-auto flex items-center gap-0.5">
          {thread ? (
            <button
              type="button"
              title={thread.root.resolved ? 'Reopen' : 'Mark resolved'}
              className={cn('flex h-9 w-9 items-center justify-center rounded-lg hover:bg-layer', thread.root.resolved ? 'text-emerald-500' : 'text-fg-muted hover:text-fg')}
              onClick={onToggleResolve}
            >
              <MS icon={MS_ICON.circle_check} size={21} />
            </button>
          ) : null}
          {thread && canManage(thread.root) ? (
            <button
              type="button"
              title="Delete thread"
              className="flex h-9 w-9 items-center justify-center rounded-lg text-fg-muted hover:bg-danger/10 hover:text-danger"
              onClick={() => onDelete(thread.root.id)}
            >
              <MS icon={MS_ICON.delete} size={19} />
            </button>
          ) : null}
          <button type="button" title="Close" className="flex h-9 w-9 items-center justify-center rounded-lg text-fg-muted hover:bg-layer hover:text-fg" onClick={onClose}>
            <MS icon={MS_ICON.close} size={19} />
          </button>
        </div>
      </div>

      {/* comments — scrolls within the panel's max height when the thread is long */}
      {thread ? (
        <div className="min-h-0 flex-1 divide-y divide-line/60 overflow-y-auto">
          {renderRow(thread.root)}
          {thread.replies.map((r) => renderRow(r))}
        </div>
      ) : null}

      {/* composer */}
      <div className="flex items-end gap-2.5 border-t border-line px-4 py-3">
        <MentionInput
          value={draft}
          onChange={setDraft}
          members={members}
          placeholder={thread ? 'Reply… (@ to mention)' : 'Add a comment… (@ to mention)'}
          onSubmit={thread ? submitReply : submitNew}
          size="lg"
        />
        <button
          type="button"
          className="flex h-12 shrink-0 items-center rounded-lg bg-brand px-4 text-[17px] font-medium text-white transition-colors hover:bg-brand/90 disabled:opacity-40"
          disabled={!draft.trim()}
          onClick={thread ? submitReply : submitNew}
        >
          {thread ? 'Reply' : 'Comment'}
        </button>
      </div>
    </div>
  );
}
